import React, { useContext } from 'react';
import Modal from 'react-bootstrap/Modal';
import { createPortal } from 'react-dom';
import Dropdown from 'react-bootstrap/Dropdown';
import {
  useViewBooking,
  useUpdateBookingStatus,
  useGetAvailableTables,
  useAssignTable,
  sendTableToPos
} from '../../../../APIs/BookingManagement/booking';
import CancelBookingModal from '../CancelBooking/CancelBookingModal';
import LoadingSpinner from '../../../../components/common/LoadingSpinner/LoadingSpinner';
import { confirmAction } from '../../../../components/common/ConfirmationModal';
import { ConfigContext } from '../../../../contexts/ConfigContext';
import { notify } from '@common/NotificationService';
import usePermission from '../../../../hooks/usePermission';
import useBusinessConfig from '../../../../hooks/useBusinessConfig';
import useAuth from '../../../../auth/useAuth';
import { displayPhone } from '../../../../utils/validation/customerFields';
import './ViewBookingModal.css';
import TableNamesCell, { bookingTables, withSeated } from 'components/common/TableNamesCell/TableNamesCell';

// Status wording, StatusBadge tone and colour — the same set as the Booking
// Management list (BookingManagement.jsx); keep the two in step.
const STATUS_META = {
  pending: { label: 'Pending', tone: 'warning', color: '#f59e0b' },
  confirmed: { label: 'Confirmed', tone: 'info', color: '#3b82f6' },
  arrived: { label: 'Arrived', tone: 'sky', color: '#38bdf8' },
  sent_to_pos: { label: 'Sent to POS', tone: 'sky', color: '#38bdf8' },
  completed: { label: 'Completed', tone: 'active', color: '#10b981' },
  cancelled: { label: 'Cancelled', tone: 'danger', color: '#ef4444' },
  expired: { label: 'Expired', tone: 'neutral', color: '#9ca3af' }
};

// An amenity's icon from its name (AC, parking, Wi-Fi…), as the tablet app
// matches them; anything else gets a tick.
const amenityIcon = (name = '') => {
  const l = String(name).toLowerCase();
  if (l.includes('condition') || l.includes('a/c') || l.includes('a.c')) return 'ph-snowflake';
  if (l.includes('parking') || l.includes('valet')) return 'ph-car';
  if (l.includes('wifi') || l.includes('wi-fi') || l.includes('internet')) return 'ph-wifi-high';
  if (l.includes('pool')) return 'ph-swimming-pool';
  if (l.includes('pet')) return 'ph-paw-print';
  if (l.includes('smok')) return 'ph-cigarette';
  if (l.includes('bar')) return 'ph-wine';
  if (l.includes('restaurant') || l.includes('dining') || l.includes('food')) return 'ph-fork-knife';
  if (l.includes('tv') || l.includes('television')) return 'ph-television';
  if (l.includes('music')) return 'ph-music-notes';
  if (l.includes('wheelchair') || l.includes('accessible')) return 'ph-wheelchair';
  if (l.includes('garden') || l.includes('outdoor')) return 'ph-tree';
  if (l.includes('gym') || l.includes('fitness')) return 'ph-barbell';
  return 'ph-check-circle';
};

// The header dropdowns are drawn on <body>, above the popup, with fixed
// positioning — inside the popup they were cut off and covered by the cards
// below the header. They carry .vb2 themselves for the colour tokens.
const inBody = (node) => createPortal(node, document.body);
const FIXED = { strategy: 'fixed' };

// Line icons drawn to match the owner's reference for the Preferences and
// Pre-ordered Items sections (2026-10-17): Lucide's sparkles, shopping bag
// and package, which the Phosphor set used elsewhere has no close match for.
// 24-unit artwork, 2px stroke, in the current text colour.
const LINE_ICONS = {
  sparkles: (
    <>
      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
      <path d="M20 3v4" />
      <path d="M22 5h-4" />
      <path d="M4 17v2" />
      <path d="M5 18H3" />
    </>
  ),
  bag: (
    <>
      <path d="M16 10a4 4 0 0 1-8 0" />
      <path d="M3.103 6.034h17.794" />
      <path d="M3.4 5.467a2 2 0 0 0-.4 1.2V20a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6.667a2 2 0 0 0-.4-1.2l-2-2.667A2 2 0 0 0 17 2H7a2 2 0 0 0-1.6.8z" />
    </>
  ),
  package: (
    <>
      <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z" />
      <path d="M12 22V12" />
      <path d="m3.3 7 7.703 4.734a2 2 0 0 0 1.994 0L20.7 7" />
      <path d="m7.5 4.27 9 5.15" />
    </>
  )
};

const LineIcon = ({ name, size = 16, className = '' }) => (
  <svg
    className={`vb3-line-icon ${className}`.trim()}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {LINE_ICONS[name]}
  </svg>
);

export default function ViewBookingModal({ show, onHide, bookingId, refresh }) {
  const config = useContext(ConfigContext);
  const { has } = usePermission();
  const { ui: bizUi, isHidden } = useBusinessConfig();
  const { disabledModules = [] } = useAuth();
  // No table layout for this business — by trade, or switched off in Feature
  // Settings. Then confirming a booking must not open (or flash) the table picker.
  const tableLayoutHidden = isHidden('table_layout') || disabledModules.includes('table_layout');

  const [cancelModalShow, setCancelModalShow] = React.useState(false);
  const [isStatusChanging, setIsStatusChanging] = React.useState(false);
  const [showForceAssignModal, setShowForceAssignModal] = React.useState(false);
  const [availableFloors, setAvailableFloors] = React.useState([]);
  const [selectedTableId, setSelectedTableId] = React.useState('');
  // The picker now opens already-populated (tables are fetched first), so this
  // stays false — kept only so the modal's guarded branches read a defined value.
  const [isFetchingTables] = React.useState(false);
  const [isAssigning, setIsAssigning] = React.useState(false);

  const { booking, isLoading, refresh: viewRefresh } = useViewBooking(bookingId);
  const { updateStatus } = useUpdateBookingStatus();
  const { fetchTables } = useGetAvailableTables();
  const { assignTable } = useAssignTable();

  React.useEffect(() => {
    setShowForceAssignModal(false);
    setAvailableFloors([]);
    setSelectedTableId('');
  }, [bookingId]);

  if (!show) return null;

  // if (!booking && !isLoading) {
  //   return (
  //     <Modal show={show} onHide={onHide} centered>
  //       <Modal.Header closeButton>
  //         <Modal.Title>Booking Details</Modal.Title>
  //       </Modal.Header>
  //       <Modal.Body>No booking selected.</Modal.Body>
  //     </Modal>
  //   );
  // }

  const {
    booking_id,
    booking_no,
    booking_date,
    booking_date_formatted,
    booking_time,
    booking_time_formatted,
    booking_type,
    location,
    service_period,
    status_label,
    booking_status,
    customer,
    internal_notes,
    comment,
    amenities,
    no_of_guests,
    pre_orders,
    pre_order_total,
    has_seat,
    is_manual_assign,
    tables: assignedTables
  } = booking || {};

  const isTakeaway = booking_type === 'takeaway';

  const displayId = booking_no || booking_id || booking?.id || '-';
  const displayDate = booking_date_formatted || booking_date || booking?.date || '-';
  const displayTime = booking_time_formatted || booking_time || booking?.time || '-';
  const displayComment = internal_notes || comment || '—';

  // const locationLabel = `${booking?.city,} ${location?.name}` || '-';
  const locationLabel = [booking?.city, location?.name].filter(Boolean).join(', ') || '-';

  const statusLabel = status_label || booking_status || '-';
  // Logic reads the raw status, never the label. They used to be the same string
  // lowercased, so this worked by accident — until "sent_to_pos" started
  // rendering as "Sent to POS" and every comparison below silently missed.
  const normalizedStatus = (booking_status || status_label || '').toLowerCase();
  const preferences = amenities || booking?.preferences || [];
  const displayCustomerName =
    customer?.name && customer.name.length ? `${customer.name.charAt(0).toUpperCase()}${customer.name.slice(1)}` : 'Customer';

  const canAssignTable = normalizedStatus === 'pending' && !is_manual_assign && has('booking_confirm');
  const isManualPending = normalizedStatus === 'pending' && !!is_manual_assign;

  // Admin bookings are confirmed on creation, with no table yet — give them
  // one here, as the tablet's Assign Table does. The booking stays confirmed.
  const canAssignConfirmed =
    !isTakeaway && normalizedStatus === 'confirmed' && !has_seat && bizUi.tableAssignment && !tableLayoutHidden && has('booking_edit');
  // The picker is open for a confirmed booking: no "confirm" wording, no
  // manual assign, and it can be closed.
  const assignOnly = normalizedStatus === 'confirmed';

  // An arrived booking can be cancelled too; its tables are freed with it.
  const canCancel = ['pending', 'confirmed', 'arrived'].includes(normalizedStatus) && has('booking_cancel');

  const canArrive = normalizedStatus === 'confirmed' && has('booking_arrive');

  const canComplete =
    normalizedStatus !== 'pending' &&
    normalizedStatus !== 'cancelled' &&
    normalizedStatus !== 'completed' &&
    normalizedStatus !== 'expired' &&
    has('booking_complete');

  // The sitting this booking is at, if any. Sending to the till acts on that
  // row rather than on the booking, so without one there is nothing to send.
  const sitting = (assignedTables || []).find((t) => t.assignment_id) || null;

  // Only a table with guests at it can be handed over: they have to be seated
  // (arrived), and it must not already be with the till or finished.
  const canSendToPos =
    !isTakeaway &&
    !!sitting &&
    normalizedStatus === 'arrived' &&
    !['sent_to_pos', 'completed', 'cancelled'].includes(sitting.assignment_status) &&
    has('table_assign');

  // Only Cancel opens over the popup; the table picker replaces it instead.
  const isChildModalOpen = cancelModalShow;

  const updateBookingStatus = async (action, extraPayload = {}) => {
    try {
      const payload = {
        action,
        ...extraPayload
      };

      const res = await updateStatus(bookingId, payload);

      if (res?.success) {
        notify.success(res?.message || 'Status updated successfully', 'update');
        await refresh();
        await viewRefresh();
        return true;
      } else {
        notify.error(res?.message || 'Failed to update booking status', 'update');
        return false;
      }
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Something went wrong', 'update');
      return false;
    }
  };

  const confirmAndRun = async (title, message, actionFn) => {
    const ok = await confirmAction({
      title,
      message,
      confirmText: 'Yes',
      cancelText: 'No'
    });
    if (!ok) return false;
    try {
      setIsStatusChanging(true);
      await actionFn();
      return true;
    } finally {
      setIsStatusChanging(false);
    }
  };

  const handleMarkArrive = async () =>
    confirmAndRun('Mark as arrived?', 'Confirm that the guest has arrived?', async () => {
      await updateBookingStatus('arrive');
    });

  const handleMarkComplete = async () =>
    confirmAndRun('Mark as complete?', 'Do you want to mark this booking as complete?', async () => {
      await updateBookingStatus('complete');
    });

  const handleSendToPos = async () =>
    confirmAndRun('Send to POS?', 'The table will be handed to the till so the bill can be settled.', async () => {
      try {
        const res = await sendTableToPos(sitting?.assignment_id);

        if (res?.success) {
          // The message names the open bill when there is one, so the cashier
          // knows what they are about to be handed.
          const bill = res?.open_bill;
          notify.success(bill ? `Sent to POS — bill ${bill.bill_no} ($${bill.total_amount})` : res.message || 'Sent to POS', 'update');
          await refresh();
          await viewRefresh();
          return;
        }

        notify.error(res?.message || 'Could not send this table to the POS', 'update');
      } catch (err) {
        notify.error(err?.response?.data?.message || 'Could not send this table to the POS', 'update');
      }
    });

  const handleConfirmClick = async () => {
    // Trades with no floor layout have no table to assign, so the picker would
    // open empty with no way out. Confirm directly instead — deliberately NOT
    // the manual-assign path, which leaves the booking pending (see
    // BookingManagementController: "mark as manual assign → stays pending"),
    // and would strand every salon booking as unconfirmed.
    if (!bizUi.tableAssignment || tableLayoutHidden) {
      setIsStatusChanging(true);
      await updateBookingStatus('confirm');
      setIsStatusChanging(false);
      return;
    }

    // Fetch the layout BEFORE opening the picker — opening it first and closing
    // it once the fetch comes back empty is what made the modal flash. The
    // Confirm button shows its disabled/busy state while this runs.
    setIsStatusChanging(true);
    setSelectedTableId('');
    const result = await fetchTables(bookingId);
    setIsStatusChanging(false);

    // A restaurant that has not drawn its floor plan yet — no floors, or floors
    // with no tables on them. The picker would open empty and the only way out
    // would be manual assign, which leaves the booking pending forever. Nothing
    // is assignable and nothing ever will be until a layout exists, so confirm.
    if (!result.hasLayout) {
      setIsStatusChanging(true);
      await updateBookingStatus('confirm');
      setIsStatusChanging(false);
      return;
    }

    // Layout exists — now open the picker, already populated.
    setAvailableFloors(result.floors || []);
    setShowForceAssignModal(true);
  };

  // Confirmed booking without a table: open the same picker, fetched first.
  const handleAssignClick = async () => {
    setIsStatusChanging(true);
    setSelectedTableId('');
    const result = await fetchTables(bookingId);
    setIsStatusChanging(false);

    if (!result.hasLayout) {
      notify.error('No floor plan with tables is set up for this location');
      return;
    }

    setAvailableFloors(result.floors || []);
    setShowForceAssignModal(true);
  };

  const handleAssignTable = async () => {
    if (!selectedTableId) return;
    setIsAssigning(true);
    try {
      const res = await assignTable(bookingId, { table_id: Number(selectedTableId) });
      if (res?.success) {
        notify.success(res?.message || (assignOnly ? 'Table assigned' : 'Table assigned and booking confirmed'));
        setShowForceAssignModal(false);
        await refresh();
        await viewRefresh();
      } else {
        notify.error(res?.message || 'Failed to assign table');
      }
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Something went wrong');
    }
    setIsAssigning(false);
  };

  const handleManualAssign = async () => {
    setIsAssigning(true);
    try {
      const res = await assignTable(bookingId, { manual: true });
      if (res?.success) {
        notify.success(res?.message || 'Booking marked for manual assignment');
        setShowForceAssignModal(false);
        await refresh();
        await viewRefresh();
      } else {
        notify.error(res?.message || 'Failed to set manual assignment');
      }
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Something went wrong');
    }
    setIsAssigning(false);
  };

  // ── Display ──────────────────────────────────────────────────────────────
  // Built from the tablet app's Booking Details popup (cld_merchant/youslotz:
  // pos_booking/view/widget/bookings_view.dart) — the same content and
  // actions — restyled for the web: see .vb3 in ViewBookingModal.css.
  const statusMeta = STATUS_META[normalizedStatus];

  // The steps in the gear menu, each only when it can be taken now.
  const menuActions = [
    // Takeaway has no arrival step.
    !isTakeaway &&
      canArrive && {
        key: 'arrive',
        label: 'Mark as Arrive',
        icon: 'ph-user-check',
        color: STATUS_META.arrived.color,
        run: handleMarkArrive
      },
    canSendToPos && {
      key: 'pos',
      label: 'Send to POS',
      icon: 'ph-cash-register',
      color: STATUS_META.sent_to_pos.color,
      run: handleSendToPos
    },
    canComplete && {
      key: 'complete',
      label: 'Mark as Complete',
      icon: 'ph-check-fat',
      color: STATUS_META.completed.color,
      run: handleMarkComplete
    },
    canCancel && { key: 'cancel', label: 'Cancel Booking', icon: 'ph-trash', color: '#dc2626', run: () => setCancelModalShow(true) }
  ].filter(Boolean);

  // The journey a booking takes, for the progress stepper. Takeaway skips the
  // seated steps. Cancelled and expired are not on it — they get a banner.
  const steps = isTakeaway
    ? [
        { key: 'pending', label: 'Pending', icon: 'ph-hourglass-simple' },
        { key: 'confirmed', label: 'Confirmed', icon: 'ph-check-circle' },
        { key: 'completed', label: 'Completed', icon: 'ph-flag-checkered' }
      ]
    : [
        { key: 'pending', label: 'Pending', icon: 'ph-hourglass-simple' },
        { key: 'confirmed', label: 'Confirmed', icon: 'ph-check-circle' },
        { key: 'arrived', label: 'Arrived', icon: 'ph-user-check' },
        { key: 'sent_to_pos', label: 'At POS', icon: 'ph-cash-register' },
        { key: 'completed', label: 'Completed', icon: 'ph-flag-checkered' }
      ];
  const stepAt = steps.findIndex((s) => s.key === normalizedStatus);
  const isClosed = normalizedStatus.includes('cancel') || normalizedStatus === 'expired';

  const initials = (displayCustomerName || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');

  const guests = no_of_guests || booking?.guests || booking?.no_of_guests;
  const empty = (v) => v === undefined || v === null || v === '' || v === '-';

  // A headline fact under the hero: icon, small caption, big value.
  const fact = (icon, label, value) => (
    <div className="vb3-fact">
      <span className="vb3-fact-icon">
        <i className={`ph ${icon}`} />
      </span>
      <span className="vb3-fact-label">{label}</span>
      <span className="vb3-fact-value">{empty(value) ? '—' : value}</span>
    </div>
  );

  // A detail row: tinted icon tile, caption over value; `href` makes the value
  // a link (call / email).
  const detail = (icon, label, value, href) => (
    <div className="vb3-detail">
      <span className="vb3-detail-icon">
        <i className={`ph ${icon}`} />
      </span>
      <span className="vb3-detail-text">
        <span className="vb3-detail-label">{label}</span>
        {empty(value) ? (
          <span className="vb3-detail-value">—</span>
        ) : href ? (
          <a className="vb3-detail-value vb3-link" href={href}>
            {value}
          </a>
        ) : (
          <span className="vb3-detail-value">{value}</span>
        )}
      </span>
    </div>
  );

  // A section's heading: icon tile, title, and an optional note on the right.
  const sectionTitle = (label, icon, aside) => (
    <div className="vb3-section-title">
      {icon && (
        <span className="vb3-section-icon">
          {/* A Phosphor class name, or an element (the line icons above). */}
          {typeof icon === 'string' ? <i className={`ph ${icon}`} /> : icon}
        </span>
      )}
      {label}
      {aside && <span className="vb3-section-aside">{aside}</span>}
    </div>
  );

  const hasComment = !!booking?.customer_comment?.trim();
  const customerComment = (
    <div className="vb3-section">
      {sectionTitle('Customer comment', 'ph-chat-circle-text')}
      <div className={`vb3-quote${hasComment ? '' : ' is-empty'}`}>
        <i className="ph ph-quotes vb3-quote-mark" />
        <p>{hasComment ? booking.customer_comment : 'No comment from the customer'}</p>
      </div>
    </div>
  );

  const tableValue =
    has_seat && assignedTables?.length > 0 ? (
      // Host table, then any merged in, as "T-01 +2", with the seats the
      // party fills at each.
      <TableNamesCell tables={withSeated(bookingTables(assignedTables), guests)} showSeated />
    ) : is_manual_assign ? (
      <span className="vb3-pending-app">Pending App</span>
    ) : null;

  return (
    <>
      {/* Steps aside while the table picker is open, and comes back when it
          closes — assigned or cancelled. */}
      <Modal
        show={show && !showForceAssignModal}
        onHide={onHide}
        centered
        className={`vb2 vb3 view-booking-modal ${isChildModalOpen ? 'is-blurred' : ''}`}
        backdropClassName="vb2-backdrop"
        // Its dropdowns live on <body>; without this the popup would pull
        // focus back from them.
        enforceFocus={false}
      >
        <Modal.Body>
          {/* ── Hero: who and what, on the brand gradient ── */}
          <div className="vb3-hero" style={{ '--vb3-status': statusMeta?.color || '#9ca3af' }}>
            <span className="vb3-hero-glow" aria-hidden="true" />
            <div className="vb3-hero-top">
              <span className="vb3-kicker">
                <i className="ph ph-calendar-check" />
                Booking Details
              </span>
              {!isLoading && <span className="vb3-id">#{displayId}</span>}

              <div className="vb3-hero-controls">
                {!isLoading && !isTakeaway && (
                  <>
                    <Dropdown align="end">
                      <Dropdown.Toggle
                        as="button"
                        type="button"
                        className="vb3-glass-btn arrow-none"
                        aria-label="Admin comment"
                        title="Admin comment"
                      >
                        <i className="ph ph-chat-teardrop-text" />
                      </Dropdown.Toggle>
                      {inBody(
                        <Dropdown.Menu className="vb2 vb2-pop vb2-comment-pop" popperConfig={FIXED}>
                          <div className="vb2-pop-title">Admin&apos;s Comment</div>
                          <div className="vb2-comment-box">{displayComment}</div>
                        </Dropdown.Menu>
                      )}
                    </Dropdown>
                  </>
                )}

                {/* The gear shows for takeaway too: Cancel Booking lives only here. */}
                {!isLoading && (
                  <>
                    <Dropdown align="end">
                      <Dropdown.Toggle
                        as="button"
                        type="button"
                        className="vb3-glass-btn arrow-none"
                        aria-label="Booking actions"
                        title="Booking actions"
                      >
                        <i className="ph ph-gear-six" />
                      </Dropdown.Toggle>
                      {inBody(
                        <Dropdown.Menu className="vb2 vb2-pop vb2-menu" popperConfig={FIXED}>
                          {menuActions.length ? (
                            menuActions.map((a) => (
                              <Dropdown.Item key={a.key} as="button" type="button" disabled={isStatusChanging} onClick={a.run}>
                                <i className={`ph ${a.icon}`} style={{ color: a.color }} />
                                {a.label}
                              </Dropdown.Item>
                            ))
                          ) : (
                            <div className="vb2-menu-empty">No actions available</div>
                          )}
                        </Dropdown.Menu>
                      )}
                    </Dropdown>
                  </>
                )}
                <button type="button" className="vb3-glass-btn" aria-label="Close" onClick={onHide}>
                  <i className="ph ph-x" />
                </button>
              </div>
            </div>

            {!isLoading && (
              <div className="vb3-hero-main">
                <span className="vb3-avatar">{initials || '?'}</span>
                <div className="vb3-who">
                  <div className="vb3-name">{displayCustomerName}</div>
                  <div className="vb3-tags">
                    <span className="vb3-status">
                      <span className="vb3-status-dot" />
                      {statusMeta?.label || statusLabel}
                    </span>
                    <span className="vb3-tag">
                      <i className={`ph ${isTakeaway ? 'ph-bag' : 'ph-fork-knife'}`} />
                      {isTakeaway ? 'Takeaway' : 'Dine In'}
                    </span>
                    {isManualPending && (
                      <span className="vb3-tag vb3-tag--warn">
                        <i className="ph ph-device-mobile" />
                        Pending App
                      </span>
                    )}
                  </div>
                </div>

                {/* What can be done next — in the hero, beside the customer,
                    so it is the first thing seen and costs no extra row. */}
                <div className="vb3-hero-actions">
                  {!isTakeaway && canAssignTable && !isManualPending && (
                    <button type="button" className="vb3-hero-btn" disabled={isStatusChanging} onClick={handleConfirmClick}>
                      <i className="ph ph-check-circle" />
                      Confirm Booking
                    </button>
                  )}
                  {canAssignConfirmed && (
                    <button type="button" className="vb3-hero-btn" disabled={isStatusChanging} onClick={handleAssignClick}>
                      <i className="ph ph-plus-circle" />
                      Assign Table
                    </button>
                  )}
                  {/* A takeaway has no gear menu, so its Complete sits here. */}
                  {isTakeaway && canComplete && (
                    <button type="button" className="vb3-hero-btn" disabled={isStatusChanging} onClick={handleMarkComplete}>
                      <i className="ph ph-check-circle" />
                      Complete
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="py-5 d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
              <LoadingSpinner />
            </div>
          ) : (
            <div className="vb3-content">
              {/* ── Booking progress, lifted over the hero's edge ── */}
              <div className="vb3-section vb3-progress-card">
                {sectionTitle('Booking Progress', 'ph-path')}
                {isClosed || stepAt < 0 ? (
                  <div className="vb3-closed" style={{ '--vb3-c': statusMeta?.color || '#9ca3af' }}>
                    <i className={`ph ${normalizedStatus === 'expired' ? 'ph-hourglass-simple-low' : 'ph-x-circle'}`} />
                    <span>
                      <strong>{statusMeta?.label || statusLabel}</strong>
                      {normalizedStatus === 'expired' ? ' — this booking has expired.' : ' — this booking was cancelled.'}
                    </span>
                  </div>
                ) : (
                  <ol className="vb3-steps" style={{ '--vb3-n': steps.length, '--vb3-at': stepAt }}>
                    <span className="vb3-steps-line" aria-hidden="true">
                      <span className="vb3-steps-fill" />
                    </span>
                    {steps.map((s, i) => (
                      <li
                        key={s.key}
                        className={`vb3-step${i < stepAt ? ' is-done' : ''}${i === stepAt ? ' is-current' : ''}`}
                        // Amber at the first stage to dark green at the last.
                        style={{
                          '--ic': `color-mix(in srgb, var(--vb3-step-end) ${Math.round((i / (steps.length - 1)) * 100)}%, #f59e0b)`
                        }}
                      >
                        <span className="vb3-step-dot">
                          <i className={`ph ${i < stepAt ? 'ph-check' : s.icon}`} />
                        </span>
                        <span className="vb3-step-label">{s.label}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              {/* Two columns: the booking on the left, what the customer
                  asked for on the right. */}
              <div className="vb3-columns">
                <div className="vb3-col">
                  {/* ── Basic details: when, for how many, where ── */}
                  <div className="vb3-section">
                    {sectionTitle('Basic details', 'ph-list-bullets')}
                    <div className="vb3-info">
                      {fact('ph-calendar-blank', 'Date', displayDate)}
                      {fact('ph-clock', 'Time', displayTime)}
                      {fact('ph-fork-knife', 'Period', service_period?.name)}
                      {!isTakeaway && fact('ph-users-three', 'Guests', guests)}
                      {!isTakeaway && fact('ph-chair', 'Table', tableValue)}
                    </div>
                  </div>

                  {/* ── Contact & venue ── */}
                  <div className="vb3-section">
                    {sectionTitle('Contact & Location', 'ph-address-book')}
                    <div className="vb3-details">
                      {detail('ph-phone', 'Phone number', displayPhone(customer?.phone), customer?.phone ? `tel:${customer.phone}` : null)}
                      {detail('ph-envelope-simple', 'Email', customer?.email, customer?.email ? `mailto:${customer.email}` : null)}
                      <div className="vb3-details-wide">{detail('ph-map-pin-line', 'Location', locationLabel)}</div>
                    </div>
                  </div>
                </div>

                <div className="vb3-col">
                  {customerComment}

                  {/* ── Preferences ── */}
                  {Array.isArray(preferences) && preferences.length > 0 && (
                    <div className="vb3-section">
                      {sectionTitle('Preferences & Amenities', <LineIcon name="sparkles" />)}
                      <div className="vb3-chips">
                        {preferences.map((pref) => {
                          const name = pref.name || pref;
                          return (
                            <span key={pref.id || name} className="vb3-chip">
                              <i className={`ph ${amenityIcon(name)}`} />
                              {name}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* ── Pre-ordered items ── */}
                  <div className="vb3-section">
                    {sectionTitle(
                      'Pre-ordered Items',
                      <LineIcon name="bag" />,
                      `${(pre_orders || []).length} ${(pre_orders || []).length === 1 ? 'item' : 'items'}`
                    )}
                    {Array.isArray(pre_orders) && pre_orders.length > 0 ? (
                      <div className="vb2-po">
                        <div className="vb2-po-row vb2-po-head">
                          <span>Item</span>
                          <span className="vb2-po-qty">Qty</span>
                          <span className="vb2-po-num">Price</span>
                          <span className="vb2-po-num">Total</span>
                        </div>
                        {pre_orders.map((item) => (
                          <div key={item.id} className="vb2-po-row">
                            <span>
                              <span className="vb2-po-name">{item.name}</span>
                              {item.category && <span className="vb2-po-cat">{item.category}</span>}
                            </span>
                            <span className="vb2-po-qty">{item.quantity}</span>
                            <span className="vb2-po-num vb2-po-price">{Number(item.unit_price).toFixed(2)}</span>
                            <span className="vb2-po-num vb2-po-total">{Number(item.subtotal).toFixed(2)}</span>
                          </div>
                        ))}
                        <div className="vb2-po-sum">
                          <span>Total</span>
                          <span>${Number(pre_order_total ?? 0).toFixed(2)}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="vb3-empty">
                        <LineIcon name="package" size={26} />
                        No pre-ordered items for this booking
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <CancelBookingModal
        show={cancelModalShow}
        booking={booking}
        onHide={() => setCancelModalShow(false)}
        onSubmit={async ({ mode, reason }) => {
          setCancelModalShow(false);

          await updateBookingStatus('cancel', {
            cancellation_source: mode,
            cancellation_reason: reason
          });
        }}
        config={config}
      />

      {/* Table picker — no dismiss when confirming (assign or manual assign);
          a confirmed booking's Assign Table can be closed. */}
      <Modal
        show={showForceAssignModal}
        centered
        backdrop="static"
        keyboard={false}
        className="vb2 vb2-tp"
        backdropClassName="vb2-backdrop"
      >
        <Modal.Body>
          <div className="vb2-tp-head">
            {/* The View Booking header's ground: mint corner, yellow dot, sand curve. */}
            <span className="vb2-tp-glow" aria-hidden="true" />
            <span className="vb2-tp-head-icon">
              <i className="ph ph-chair" />
            </span>
            <div className="vb2-head-text">
              <div className="vb2-title">{assignOnly ? 'Assign Table' : 'Assign Table to Confirm'}</div>
              <div className="vb2-sub">
                {assignOnly ? 'Select a table for this booking' : 'Select a table below — booking confirms on assignment'}
              </div>
            </div>
            {assignOnly && (
              <button
                type="button"
                className="vb2-close"
                aria-label="Close"
                disabled={isAssigning}
                onClick={() => setShowForceAssignModal(false)}
              >
                <i className="ph ph-x" />
              </button>
            )}
          </div>

          <div className="vb2-tp-body">
            {isFetchingTables ? (
              <div className="py-4 d-flex justify-content-center">
                <LoadingSpinner />
              </div>
            ) : availableFloors.length > 0 ? (
              availableFloors.map((floor) => (
                <section key={floor.floor_id} className="vb2-tp-floor">
                  <div className="vb2-tp-floor-head">
                    <i className="ph ph-map-trifold" />
                    <span className="vb2-tp-floor-name">{floor.floor_name}</span>
                    <span className="vb2-tp-floor-count">
                      {floor.tables.length} {floor.tables.length === 1 ? 'table' : 'tables'}
                    </span>
                  </div>
                  <div className="vb2-tp-grid">
                    {floor.tables.map((t) => {
                      const sel = String(t.id) === String(selectedTableId);
                      return (
                        <button
                          key={t.id}
                          type="button"
                          className={`vb2-tp-table${sel ? ' is-selected' : ''}`}
                          aria-pressed={sel}
                          onClick={() => setSelectedTableId(String(t.id))}
                        >
                          <span className="vb2-tp-check" aria-hidden="true">
                            <i className="ph ph-check" />
                          </span>
                          <span className="vb2-tp-icon">
                            <i className="ph ph-chair" />
                          </span>
                          <span className="vb2-tp-name">{t.name}</span>
                          <span className="vb2-tp-seats">
                            <i className="ph ph-users" />
                            {t.capacity} seats
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ))
            ) : (
              <div className="vb2-tp-empty">
                <span className="vb2-tp-empty-icon">
                  <i className="ph ph-chair" />
                </span>
                <p>No tables with matching capacity are free at this time.</p>
                {!assignOnly && <p className="vb2-tp-hint">Use manual assign to flag for later.</p>}
              </div>
            )}
          </div>

          <div className="vb2-tp-foot">
            {/* What will be assigned, so the choice is confirmed before the click. */}
            <div className="vb2-tp-picked">
              {(() => {
                const picked = availableFloors.flatMap((f) => f.tables).find((t) => String(t.id) === String(selectedTableId));
                return picked ? (
                  <>
                    <i className="ph ph-check-circle" />
                    <span>
                      <strong>{picked.name}</strong> · {picked.capacity} seats
                    </span>
                  </>
                ) : (
                  <span className="vb2-tp-picked-none">No table selected</span>
                );
              })()}
            </div>
            <div className="vb2-tp-buttons">
              {assignOnly ? (
                <button type="button" disabled={isAssigning} onClick={() => setShowForceAssignModal(false)} className="vb2-cancel">
                  Cancel
                </button>
              ) : (
                <button type="button" disabled={isAssigning} onClick={handleManualAssign} className="vb2-chip-action vb2-chip-action--warn">
                  <i className="ph ph-device-mobile" />
                  {isAssigning ? 'Saving...' : 'Manual Assign'}
                </button>
              )}
              <button
                type="button"
                disabled={!selectedTableId || isAssigning || isFetchingTables}
                onClick={handleAssignTable}
                className="btn btn-primary vb2-primary"
              >
                <i className="ph ph-check" />
                {isAssigning ? 'Assigning...' : assignOnly ? 'Assign' : 'Assign & Confirm'}
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
